import { Injectable } from '@angular/core';
import { App, ModalController, NavOptions, Platform } from 'ionic-angular';
import { PageDirectoryProvider } from '../page-directory/page-directory';
import { INavInfo } from '../../model/models/layout/nav-info.interface';
import { LoggerProvider, LoggerLevel, LoggerEvent } from '../logger/logger';
import { TranslateService } from '@ngx-translate/core';
import { InAppBrowser, InAppBrowserOptions } from '@ionic-native/in-app-browser';
import { APP_CONFIG, Region } from '../../environment';
import { ColorProvider } from '../color/color';
import { buildDefaultIabOptions } from './iab-options';
import { ProfileNotification } from '../../model/types/profile-notification-type';
import { SafariViewController, SafariViewControllerOptions} from '@ionic-native/safari-view-controller';
import { SafariViewControllerOptionsExtended } from '../../model/models/payment-methods';
import { NativeLiquidGlassBarProvider } from '../native/native-liquid-glass-bar';
import { VueBridgeState } from '../vue/vue-helpers';
declare var window: any;
@Injectable()
export class RouteProvider {
  static ready = false;
  navOptions;
  private currentPageName;
  previousPageName;
  lastPushedPage: INavInfo;
  transitionRootPage: INavInfo;
  replaceOrigPage: INavInfo;
  replaceOrigPageAfter: boolean = true;
  currentPageType: string;
  transitionPromise;
  // set by WalletProvider; avoids a DI cycle from importing it here. see setRoot().
  static walletSupportEnabled = false;
  callbacks = [];
  maskValues = [
    'password',
    'currentpassword',
    'newpassword',
    'confirmpassword',
    'cardnumber',
    'cvv',
    'cvn',
    'cardsecuritycode',
    'accountexpirationmonth',
    'accountexpirationyear',
    'accountnumber',
    'securityquestionanswer1',
    'securityquestionanswer2',
    'securityquestionanswer3',
    'answer',
    'securityquestions',
    'fingerprint',
    'access-token',
    'access_token',
    'accesstoken',
    'refreshtoken',
    'refresh_token',
    'token',
    'devicetoken',
    'device_token',
    'devicesimcard',
    'pin',
    'transactionpin',
    'newtransactionpin',
    'orderinput',
    'latitude',
    'longitude',
    'devicelatitude',
    'devicelongitude'
  ];
  maskValuesIdentifiableInfo = [
    'username',
    "firstName",
    "middleName",
    "lastName",
    "lastName1",
    "lastName2",
    "phoneNumber",
    "phonenumber",
    "phonenumbervalue",
    "mobilephone",
    "otp",
    "providedfields",
    "firstname",
    "lastname",
    "address",
    "unitNumber",
    "city",
    "state",
    "address",
    "postalCode",
    "securityQuestions",
    "areaCode",
    "number",
    "phoneNumberNoAreaCode",
    "phoneNumberFormatted",
    "areaCode",
    "pin",
    "bankAccountNumber",
    "bankAccountNumberUnitary",
    "bankBicSwift",
    "telephoneNumber",
    "telephoneNumberCell",
    "idDocumentNumber",
    "mobileWalletAccountNo",
    "billingAddress",
    "billingCity",
    "billingState",
    "billingPostalCode",
    "zipcode"
  ]
  filterValues;
  backButtonUnsubscribeCallback;
  pendingNavParams = null;

  constructor(private app: App,
    private pageDirectory: PageDirectoryProvider,
    private modalCtrl: ModalController,
    private translate: TranslateService,
    private platform: Platform,
    private tabsView: SafariViewController,
    private iab: InAppBrowser,
    private liquidGlassBar: NativeLiquidGlassBarProvider) {
    this.navOptions = {
      animation: 'ios-transition'
    };

  }

  get pages(): PageDirectoryProvider {
    return this.pageDirectory;
  }

  isStartPage() {
    if (this.getLastActivePage() === this.pages.SigninPage.page ||
        this.getLastActivePage() === this.pages.PreLoginPage.page ||
        this.getLastRootPage() === this.pages.SigninPage.page ||
        this.getLastRootPage() === this.pages.PreLoginPage.page)
      return true;
    return false;
  }

  getLastRootPage() {
    if (this.transitionRootPage)
      return this.transitionRootPage.page;

    const navCtrl = this.app.getRootNavs()[0];
    const lastView = navCtrl._views[navCtrl._views.length - 1];
    const page = this.extractPageName(lastView);
    return page;
  }

  getPageType() {
    return this.currentPageType;
  } 

  getPreviousPageName() {
    return this.previousPageName;
  }

  getLastActivePage() {
    if (this.transitionRootPage)
      return this.transitionRootPage.page;

    const navCtrl = this.app.getActiveNavs()[0];
    if (navCtrl._views && navCtrl._views.length > 0) {
      const lastView = navCtrl._views[navCtrl._views.length - 1];
      const page = this.extractPageName(lastView);
      return page;
    }
    return null;
  }

  getLastActivePageInstance() {
    if (this.transitionRootPage)
      return this.transitionRootPage.page;

    const navCtrl = this.app.getActiveNavs()[0];
    if (navCtrl._views && navCtrl._views.length > 0) {
      const lastView = navCtrl._views[navCtrl._views.length - 1];
      return lastView.instance;
    }
    return null;
  }

  async getCurrentPageName() {
    await this.transitionPromise;

    return this.currentPageName;
  }

  getCurrentTab() {
    const navCtrl =this.app.getRootNavs()[0];
    if (!navCtrl._views || navCtrl._views.length === 0)
      return null;
    if (!navCtrl._views[0].instance || !navCtrl._views[0].instance.tabControl)
      return null;
    const tab = navCtrl._views[0].instance.tabControl.getSelected();
    if (!tab)
      return null;
    return tab;
  }

  getCurrentTabName() {
    const tab = this.getCurrentTab();
    if (tab)
      return tab.root;
    return null;
  }

  getCurrentTabPage() {
    const tab = this.getCurrentTab();
    if (!tab)
      return null;
    if (!tab._views || tab._views.length === 0)
      return null;
    return tab._views[0].instance;
  }

  async getLastActivePageAsync() {
    if (this.transitionPromise)
      await this.transitionPromise;
    return this.getLastActivePage();
  }

  async waitForTransitions() {
    if (this.transitionPromise)
      await this.transitionPromise;
  }

  getLastPushedPage() {
    return this.lastPushedPage;
  }

  isLastPushedPage(page) {
    if (this.lastPushedPage) {
      // remove isModalPage from comparison as it is not relevant for the last pushed page and has been added eventually in openModal
      const last = JSON.stringify({ ...this.lastPushedPage, isModalPage: null });
      const compare = JSON.stringify({ ...page, isModalPage: null });
      if (last === compare)
        return true;
    }
    return false;
  }

  checkPageIsModal() {
    return this.getPageType() === 'modal';
  }

  extractPageName(view) {
    // VERY IMPORTANT: if the build flags are set to minify JS the page name will be lost (i.e. "SigninPage" will be "n") - hence we try to set the navInfo in all views and use that
    if (!view)
      return 'Unknown';
    if (view.navInfo)
      return view.navInfo.page;
    if (view.data && view.data.component)
      return view.data.component.name;
    return view.name;
  }

  mergeParams(page: INavInfo, params) {
    let merged = { ...page.params, ...params }
    if (this.pendingNavParams)
      merged = { ...merged, ...this.pendingNavParams };
    this.pendingNavParams = null;
    return merged;
  }

  async updateLastView(navInfo) {
    await this.transitionPromise;
    const navCtrl = this.app.getActiveNavs()[0];
    if (navCtrl && navCtrl._views && navCtrl.length() > 0)
      navCtrl._views[navCtrl.length() - 1]['navInfo'] = navInfo;
  }

  pushOrReplace(replace: boolean = false, page: INavInfo, params = null, origPage: INavInfo = null, withAnimation = true) {    
    if (replace)
      return this.replace(page, params, origPage);
    return this.push(page, params, withAnimation);
  }

  push(page: INavInfo, params = null, withAnimation = true, transition = 'ios-transition', duration = null) {
    const navCtrl = this.app.getActiveNavs()[0];
    if (this.checkDuplicateNavigation(navCtrl, page))
      return;

    const log = this.logLeave('Push');
    if (!log)
      return;
    if (!this.logEnter(page, params, 'Push', log))
      return;
    params = this.mergeParams(page, params);
    this.navOptions.direction = 'forward';
    this.navOptions.animation = transition;
    if (duration) {
      this.navOptions.duration = duration;
    }
    const transitionPromise = navCtrl.push(page.page, params, (withAnimation === true) ? this.navOptions : {animate: false});
    this.setTransition(transitionPromise);
    this.updateLastView(page);
    this.lastPushedPage = page;
    this.currentPageType = 'page';
    return this.transitionPromise;
  }

  pop() {
    const log = this.logLeave('Pop');
    this.lastPushedPage = null;
    this.currentPageType = null;
    this.navOptions.direction = 'back';
    this.navOptions.animation = 'md-transition';
    const transitionPromise = this.app.getActiveNavs()[0].pop(this.navOptions);
    this.setTransition(transitionPromise);
    this.transitionPromise.then(
      ()=>{
        const navCtrl = this.app.getActiveNavs()[0]; // get the navcontroller AFTER the navigation stack has been adjusted as it might have changed (i.e. popping a modal window)
        if (navCtrl.length() > 0)
          this.logEnter({ page: this.extractPageName(navCtrl._views[navCtrl.length() - 1]) }, null, 'Pop', log);
      }
    )

  }

  popTo(page: INavInfo, fromModal?: boolean, params: any = null, pageId: any = null, reverse: boolean = false) {
    const navCtrl = fromModal ? this.app.getRootNav() : this.app.getActiveNavs()[0];
    const numberOfViews = navCtrl.length();
    let indexOfPage = -1;

    if (navCtrl._views) {
      if(pageId) {
        let index = -1;
        if (reverse)
          index = navCtrl._views.findLastIndex(view => view.navInfo.id === pageId);
        else
          index = navCtrl._views.findIndex(view => view.navInfo.id === pageId);
        indexOfPage = index >= 0 ? index : this.getPageIndex(navCtrl._views, page, reverse);
      }
      else indexOfPage = this.getPageIndex(navCtrl._views, page, reverse);
    } else 
      indexOfPage = 0;

    let log = null;
    this.lastPushedPage = null;
    this.currentPageType = null;
    this.navOptions.direction = 'back';
    this.navOptions.animation = 'md-transition';
    for (let i = (numberOfViews - 1); i >= indexOfPage + 1; i--) {
      log = this.logLeave('PopTo', navCtrl._views[i], log);
    }
    this.logEnter(page, null, 'PopTo', log);
    if(fromModal) {
      const nav = this.app.getActiveNavs()[0];
      nav.remove(0, 1, this.navOptions);
    }
    // TODO !!!!!!!!!!!!!!!!!!!!! NOT SURE THAT WORKS PROPERLY - AVOID USING IT
    if(params) {
      navCtrl._views[indexOfPage].data.params = params;
    }
    const transitionPromise = navCtrl.remove(indexOfPage + 1, (numberOfViews - indexOfPage - 1), this.navOptions);
    this.setTransition(transitionPromise);
    return transitionPromise;
  }

  popToOrPush(page: INavInfo, params = {}){
    if(this.hasPage(page))
      this.popTo(page)
    else
      this.openModal(page, params);
  }

  getPageIndex(views, page, reverse: boolean = false) {
    if (reverse)
      return views.findLastIndex((view) => this.extractPageName(view) === page.page);
    else
      return views.findIndex((view) => this.extractPageName(view) === page.page);
  }

  hasPage(page: INavInfo, checkAlsoRoot: boolean = false) {
    const navs = [ this.app.getActiveNavs()[0] ];
    if (checkAlsoRoot)
      navs.push(this.app.getRootNav());

    for (let i=0; i < navs.length; i++) {
      const navCtrl = navs[i];
      if (!navCtrl)
        continue;

      if (navCtrl._views) {
        const indexOfPage = navCtrl._views.findIndex((view) => this.extractPageName(view) === page.page);
        if (indexOfPage >= 0)
          return true;
      }
    }
    return false;
  }

  getReplaceOrigPage() {
    return this.replaceOrigPage;
  }

  setReplaceOrigPage(origPage: INavInfo, replaceAfter: boolean = true) {
    this.replaceOrigPage = origPage;
    this.replaceOrigPageAfter = replaceAfter;
  }

  replace(page: INavInfo, params = null, origPage: INavInfo = null, replaceAfter: boolean = false, backwards: boolean = false, withAnimation = true) {
    const navCtrl = this.app.getActiveNavs()[0];
    if (this.checkDuplicateNavigation(navCtrl, page))
      return;

    params = this.mergeParams(page, params);

    if (!origPage) {
      origPage = this.replaceOrigPage;
      if (origPage)
        replaceAfter = this.replaceOrigPageAfter;
    }
    this.replaceOrigPage = null;

    if (backwards) {
      this.navOptions.direction = 'back';
      this.navOptions.animation = 'md-transition';
    }
    else {
      this.navOptions.direction = 'forward';
      this.navOptions.animation = 'ios-transition';
    }
    this.lastPushedPage = page;
    const transitionPromise  = new Promise(async(resolve) => {
      await this.waitForTransitions();
      await navCtrl.push(page.page, params, (withAnimation === true) ? this.navOptions : {animate: false});
      if (backwards) {
        this.navOptions.direction = 'forward';
        this.navOptions.animation = 'ios-transition';
      }
      else {
        this.navOptions.direction = 'back';
        this.navOptions.animation = 'md-transition';
      }

      // remove previous page(s)
      const numberOfViews = navCtrl.length() - 1;
      let indexOfPage = numberOfViews - 1;
      let pages = 1;

      if (origPage) {
        if (origPage.page === this.pages.FirstPage.page)
          indexOfPage = 0;
        else
          indexOfPage = navCtrl._views.findIndex((view) => this.extractPageName(view) === origPage.page);
        if (replaceAfter)
          indexOfPage++;
        pages = numberOfViews - indexOfPage;
      }

      let log = null;
      for (let i = pages; i >= indexOfPage; i--) {
        log = this.logLeave('Replace', navCtrl._views[i], log);
      }
      this.logEnter(page, params, 'Replace', log);
      navCtrl.remove(indexOfPage, pages, (withAnimation === true) ? this.navOptions : {animate: false});
      resolve(true);
    });
    this.setTransition(transitionPromise);
    this.updateLastView(page);
    return this.transitionPromise;
  }

  closeModals() {
    try {
      let activePortal =  this.app._appRoot._overlayPortal.getActive();
      if (activePortal && activePortal.data && activePortal.data.enableBackdropDismiss)
        activePortal.dismiss();
    } catch (ex) {
      LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Warnings when closing modals', ex);
    }
  }

  setRoot(page: INavInfo, params = {}, animate = true, clearViewsArray: boolean = true) {
    if (VueBridgeState.retireStaleLandingLayer)
      VueBridgeState.retireStaleLandingLayer();
    if(APP_CONFIG.region.loginRootHomePage === 'wallethome'){
      if(page && page.page.toLowerCase().indexOf("tabs") != -1){
        page = this.pages.WalletHome;
      }
    }
    const navCtrl = this.app.getActiveNavs()[0];
    if (this.checkDuplicateNavigation(navCtrl, page))
      return;

    this.closeModals();

    params = this.mergeParams(page, params);
    const rootCtrl = this.app.getRootNavs()[0];
    this.lastPushedPage = page;
    this.transitionRootPage = page;
    const navOptions = { ...this.navOptions, animate };
    const transitionPromise = new Promise((resolve) => {
      rootCtrl.setRoot(page.page, params, navOptions)
        .then((res) => {
          this.transitionRootPage = null;
          // check if overlay exists and needs to be removed
          if (this.app.getActiveNavs()[0] === navCtrl && this.app.getRootNavs()[0] !== navCtrl) {
            LoggerProvider.info(this, 'Still open views in nav controllers - destroy them');
            let log = null;
            const promises = [];
            for (let i = (navCtrl.length() - 1); i >= 0; i--) {
              log = this.logLeave('SetRoot', navCtrl._views[i], log);
              // when something has gone wrong before remove view and dismiss won't work anymore hence let's destroy it directly to be on the safe side              
              try {
                promises.push(navCtrl._views[i]._destroy(null));
                // In rare case the promise is getting failed if we have removeViewListners.. And it's also important to clear the navCtrl._Views on setRoo
                if(clearViewsArray)
                  promises.push(navCtrl._views.splice(i, 1));
              } catch (ex) {
                LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Route destroy failed', ex);
              }
            }
            Promise.all(promises).then(() => {
              resolve(true);
            });
            this.logEnter(page, params, 'SetRoot', log);
          }
          else
          {
            resolve(true);
            this.logEnter(page, params, 'SetRoot');
          }
        })
        .catch((ex) => {
          LoggerProvider.warning(this, LoggerEvent.ProviderException, 'Warnings when trying to setRoot', ex);        
          // an emergency fallback to keep the app being able to continue with the root page if normal setRoot doesn't work
          rootCtrl.insert(0, page.page, params);
          rootCtrl.popToRoot();
        });
    });
    this.setTransition(transitionPromise);
    this.updateLastView(page);
    return this.transitionPromise;
  }

  checkDuplicateNavigation(navCtrl, page) {
    if (this.lastPushedPage && page && this.lastPushedPage.page === page.page && navCtrl.isTransitioning()) {
      LoggerProvider.info(this, 'Skipped duplicate page to ' + page.page);
      return true;
    }
    return false;
  }

  openActionSheetNew(page: INavInfo, params = {}, returnFunc: Function | null = null, cssClass = 'modal-default', preventDismiss = false, dismissPreviousSheet = false) {
    return this.openActionSheet(page, params, returnFunc, 'modal-new-design ' + cssClass, preventDismiss, dismissPreviousSheet);
  }

  openActionSheet(page: INavInfo, params = {}, returnFunc: Function | null = null, cssClass = 'modal-default', preventDismiss = false, dismissPreviousSheet = false, reEnterOnDismiss = true) {
    // remove previous alert page to prevent overlay
    if (dismissPreviousSheet) {
      if(this.lastPushedPage && this.lastPushedPage.page == 'ModalAlertPage') {
        this.pop();
      }
    }
    cssClass = 'rmt-actionsheet-modal ' + cssClass;
    if (preventDismiss)
      cssClass += ' rmt-actionsheet-modal-prevent-dismiss'
    return this.openModal(page, params, returnFunc, cssClass, true, reEnterOnDismiss);
  }

  // reEnterOnDismiss: when the modal closes, call rmtViewDidReEnter on the page below, which loads its data again.
  // Set it to false for a modal that changes no data, for example a sheet that only selects an item.
  openModal(page: INavInfo, params = {}, returnFunc: Function | null = null, cssClass = 'modal-default', withAnimation = true, reEnterOnDismiss = true) {
    const rootCtrl = this.app.getRootNavs()[0];
    const navCtrl = this.app.getActiveNavs()[0];
    if (this.checkDuplicateNavigation(navCtrl, page))
      return;

    const log = this.logLeave('OpenModal');
    if (!log)
      return;
    if (!this.logEnter(page, params, 'OpenModal', log))
      return;
    params = this.mergeParams(page, params);
    this.liquidGlassBar.notifyModalOpened();
    let modalGateClosed = false;
    const closeModalGate = () => {
      if (!modalGateClosed) {
        modalGateClosed = true;
        this.liquidGlassBar.notifyModalDismissed();
      }
    };
    const modal = this.modalCtrl.create(page.page, params, { cssClass: cssClass });
    // Released at willDismiss so the glass bar fades in during the dismiss animation.
    modal.onWillDismiss(() => closeModalGate());
    modal.onDidDismiss(async (data) => {
      closeModalGate();
      if (returnFunc) {
        returnFunc(data);
      }
      if (!reEnterOnDismiss)
        return;
      let lastPage = true;
      if (navCtrl && navCtrl._views && navCtrl._views.length > 1)
        lastPage = false;
      if (rootCtrl && 
          rootCtrl._views && 
          rootCtrl._views.length > 0 && 
          rootCtrl._views[0] && 
          rootCtrl._views[0].instance && 
          rootCtrl._views[0].instance.rmtViewDidReEnter &&
          lastPage)
        rootCtrl._views[0].instance.rmtViewDidReEnter();
      if (navCtrl && navCtrl._views && navCtrl._views.length > 0 && !lastPage) {
        await this.transitionPromise;
        window.requestAnimationFrame(()=> {
          if (navCtrl && navCtrl._views && navCtrl._views.length > 0 && navCtrl._views[navCtrl._views.length - 1].instance &&
              navCtrl._views[navCtrl._views.length - 1].instance.rmtViewDidReEnter) {
            navCtrl._views[navCtrl._views.length - 1].instance.rmtViewDidReEnter();
          }
        })
      }

    });
    const transitionPromise = modal.present({ animate: withAnimation });
    transitionPromise.catch(() => closeModalGate());
    this.setTransition(transitionPromise);
    this.updateLastView(page);
    this.lastPushedPage = { ...page, isModalPage: true }; // add isModalPage to the last pushed page to identify it as a modal
    this.currentPageType = 'modal';
    return new Promise(async(resolve) => {
      await this.transitionPromise;
      resolve(modal);
    });
  }

  async openIab(url = '', iabOptions: InAppBrowserOptions = null, phoneEmailRedirection: boolean = false, openAllURLsInExternal: boolean = false, iabTarget: string = "_default") {
    LoggerProvider.info(this, 'Open IAB: ' + url);
    url = this.changeCountryLocale(url);
    if (this.platform.is('cordova')) {
      if (iabOptions == null)
        iabOptions = buildDefaultIabOptions(this.platform, this.translate);
      if (phoneEmailRedirection && this.platform.is('ios')) {
        let isABAvailable = false;
        try {
          isABAvailable = await this.tabsView.isAvailable();
        } catch {
          LoggerProvider.info(this, 'Safari VC not supported');
        }
        if(isABAvailable) {
          LoggerProvider.info(this, 'Open with Safari VC: ' + url);
          this.openSafariViewController(url);
          return;
        }
      }

      if (openAllURLsInExternal)
        LoggerProvider.info(this, 'Open with URLs in external: ' + url);
      else if (phoneEmailRedirection)
        LoggerProvider.info(this, 'Open with phone/email redirection: ' + url);

      const browser = this.iab.create(url, iabTarget, iabOptions);
      browser.on('loadstart').subscribe((event) => {
        const url = event.url;
        if (url.startsWith('whatsapp://') || url.startsWith('https://wa.me/') || url.startsWith('https://api.whatsapp.com/')) {
          this.openSafariViewController(url);
          LoggerProvider.info(this, 'Redirecting WhatsApp link externally: ' + url);
          browser.close();
          return;
        }

        if (openAllURLsInExternal && url.indexOf('riamoneytransfer') < 0) {
          LoggerProvider.info(this, 'Handle external redirection: ' + url);
          this.iab.create(url, '_system');
          browser.close();
          return;
        }
        if (phoneEmailRedirection && (url.startsWith('http://tel:') || url.startsWith('http://mailto:'))) {
          LoggerProvider.info(this, 'Handle phone/email redirection: ' + url);
          window.open(url.substr(7), '_system');
          browser.close();
          return;
        }
      });
    } else {
      this.openModal(this.pages.HrefPage, { href: url });
    }
  }

  openSafariViewController(url) {
    const options: SafariViewControllerOptions = {
      url: url,
      tintColor: ColorProvider.getColorValue('--app-primary'), // default is ios blue
      barColor: ColorProvider.getColorValue('--gray-100'), // on iOS 10+ you can change the background color as well
      controlTintColor: ColorProvider.getColorValue('--app-primary'), // on iOS
      showDefaultShareMenuItem: false,
    };

    this.tabsView.show(options).subscribe(
      async (res) => {
        if (res.event === 'loaded') {
          LoggerProvider.info(this, 'help center view loaded');
        } else if (res.event === 'closed') {
          LoggerProvider.info(this, 'help center view closed');
        }
      },
      (err) => {
        LoggerProvider.error(this, LoggerEvent.GeneralException, 'Help center was closed', err);
      });
  }

  logEnter(page, params, text, log = null) {
    const newparams = this.maskLog(params);
    LoggerProvider.scope = page.page;
    if (text !== 'OpenModal') {
      this.currentPageName = page.page;
    }
    if (!log)
      log = this;
    log = LoggerProvider.log(log, LoggerLevel.Info, LoggerEvent.NavigationEnter, text + ' enter ' + page.page, newparams);

    for (let i = 0; i < this.callbacks.length; i++) {
      if (!this.callbacks[i](true, page.page, page, params))
        return null;
    }
    return log;
  }

  logLeave(text, view = null, log = null) {
    this.deregisterBackButton()
    if (!view) {
      const navCtrl = this.app.getActiveNavs()[0];
      if (navCtrl && navCtrl._views)
        view = navCtrl._views[navCtrl.length() - 1];
    }
    const name = this.extractPageName(view);
    if (!log)
      log = this;
    log = LoggerProvider.log(log, LoggerLevel.Info, LoggerEvent.NavigationLeave, text + ' leave ' + name);
    this.previousPageName = name;

    const navCtrl = this.app.getActiveNavs()[0];
    if (navCtrl._views)
      LoggerProvider.scope = this.extractPageName(navCtrl._views[navCtrl.length() - 1]);

    for (let i = 0; i < this.callbacks.length; i++) {
      if (!this.callbacks[i](false, name, null, null))
        return null;
    }
    return log;
  }

  maskLog(body, removeIdentifiableInfo:boolean = false) {
    if (!body)
      return null;
    // if removeIdentifiableInfo is true, we sanitize extra details before logging to logrocket
    this.filterValues = removeIdentifiableInfo ? this.maskValues.concat(this.maskValuesIdentifiableInfo): this.maskValues;

    let maskedbody;
    try {
      let bodystr;
      if (typeof body === 'string') {
        bodystr = body.toLowerCase();
        maskedbody = JSON.parse(bodystr);
      }
      else {
        // reparse the body to deep copy it (...body would still retain references which could then be changed in the masking)
        bodystr = JSON.stringify(body);
        bodystr = bodystr.toLowerCase();
        maskedbody = JSON.parse(bodystr);
      }

      let needmasking = false;
      for (let i=0; i < this.filterValues.length; i++) {
        const val = this.filterValues[i].toString();
        if (bodystr.indexOf(val) >= 0) {
          needmasking = true;
          break;
        }
      }
      if (needmasking)
        this.maskLogChilds(maskedbody);
    }
    catch {
      return `Can't mask content (length: ${body.length})`;
    }

    return JSON.stringify(maskedbody, null, 2);
  }

  maskLogChilds(body, mask = false) {
    for (const i in body) {
      const val = body[i];
      if (val) {
        let maskchilds = mask;
        this.filterValues.forEach((val) => {
          if (val.toString() === i.toLowerCase()) {
            maskchilds = true;
          }
        });

        if (typeof val === 'string') {
          if (maskchilds) {
            body[i] = 'x'.repeat(val.length);
          }
        }
        else {
          this.maskLogChilds(val, maskchilds);
        }
      }
    }
  }

  registerBackButton(callBack: ()=>void, priority) {
    this.backButtonUnsubscribeCallback = this.platform.registerBackButtonAction(callBack, priority);
  }

  deregisterBackButton() {
    if (this.backButtonUnsubscribeCallback)
      this.backButtonUnsubscribeCallback();
    this.backButtonUnsubscribeCallback = null;
  }

  subscribeBackButton(callBack: ()=>void, priority) {
   return this.platform.backButton.subscribe(priority, () => callBack());
  }

  unsubscribeBackButton(subscribtion: any) {
   if(subscribtion) subscribtion.unsubscribe();
  }

  changeCountryLocale(url){
    if(APP_CONFIG.region.defaultCountryFrom === "UK"){
      let localURL = String(url).toLocaleLowerCase();
      if(localURL && localURL !== '' && localURL !== "" && localURL.indexOf('en-uk') !== -1){
        if(url.indexOf("EN-UK") !== -1){
          url = url.replace("EN-UK", "en-gb");
        } else if(url.indexOf("en-UK") !== -1){
          url = url.replace("en-UK", "en-gb");
        } else if(url.indexOf("en-uk") !== -1){
          url = url.replace("en-uk", "en-gb");
        }
      }
    }
    return url;
  }

  addPendingNavParams(params) {
    if (!this.pendingNavParams)
      this.pendingNavParams = {};
    this.pendingNavParams = { ...this.pendingNavParams, ...params };
  }

  setTransition(transitionPromise) {
    this.transitionPromise = transitionPromise;
    this.ensureTransition(transitionPromise);
  }

  async ensureTransition(transitionPromise) {
    try {
      await transitionPromise;
      if (this.transitionPromise && this.transitionPromise !== transitionPromise) {
        LoggerProvider.warning(this, LoggerEvent.PageException, 'Transition during other transition !');
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Transition failed');
    }
  }


    async openSafariViewControllerExternal(url, logError: boolean = true, successCallBack: any, errorCallBack = null) {
    let isAvailable = false;

    const options: SafariViewControllerOptionsExtended = {
      url: url,
      tintColor: ColorProvider.getColorValue('--app-primary'), // default is ios blue
      barColor: ColorProvider.getColorValue('--gray-100'), // on iOS 10+ you can change the background color as well
      controlTintColor: ColorProvider.getColorValue('--app-primary'), // on iOS
      showDefaultShareMenuItem: false,
      redirectString: "riamoneytransfer://"
    };

    if (this.platform.is('ios')) {
      window.plugins.ASWebAuthSession.start("riamoneytransfer", options.url,
        async (msg) => {
          successCallBack(msg);
        }, (err) => {
          if (logError) {
            LoggerProvider.error(this, LoggerEvent.GeneralException, 'ASWebAuthSession error', err);
          } else {
            LoggerProvider.info(this, 'ASWebAuthSession doesnt get proper redirect', err);
          }
          if (errorCallBack)
            errorCallBack(err);
        });
    } else {
      try {
        isAvailable = await this.tabsView.isAvailable();
        LoggerProvider.info(this, 'safari VC view is available check', isAvailable);
        if (!isAvailable) {
          LoggerProvider.info(this, 'safari VC view is not available');
          this.iab.create(url, "_system");
          return;
        }
      } catch (ex) {
        LoggerProvider.info(this, 'safari VC view is not available');
        this.iab.create(url, "_system");
        return;
      }

      if (isAvailable) {
        this.tabsView.show(options).subscribe(
          async (res) => {
            if (res.event === 'loaded') {
              LoggerProvider.info(this, 'safari VC view loaded');
            } else if (res.event === 'closed') {
              LoggerProvider.info(this, 'safari VC view closed', res);
              successCallBack(res, true)
            } 
          },
          (err) => {
            LoggerProvider.error(this, LoggerEvent.GeneralException, 'safari VC was closed', err);
            if(errorCallBack)
              errorCallBack(err);
          });
      }
    }
  }

  pushOrModal(modal: boolean = false, page: INavInfo, params = null, returnFunc: Function | null = null) {    
    if (modal)
      return this.openModal(page, params, returnFunc);
    return this.push(page, {
      ...params,
      modalDismiss: () => { returnFunc ? returnFunc() : null; }
    });
  }

  pushOnRoot(page: INavInfo, params = null, withAnimation = true, transition = 'ios-transition', duration = null) {
    const navCtrl = this.app.getRootNavs()[0];
    if (this.checkDuplicateNavigation(navCtrl, page))
      return;

    const log = this.logLeave('PushOnRoot');
    if (!log)
      return;
    if (!this.logEnter(page, params, 'PushOnRoot', log))
      return;
    params = this.mergeParams(page, params);
    this.navOptions.direction = 'forward';
    this.navOptions.animation = transition;
    if (duration) {
      this.navOptions.duration = duration;
    }
    const transitionPromise = navCtrl.push(page.page, params, (withAnimation === true) ? this.navOptions : {animate: false});
    this.setTransition(transitionPromise);
    this.updateLastView(page);
    this.lastPushedPage = page;
    this.currentPageType = 'page';
    return this.transitionPromise;
  }

  popOnRoot() {
    const log = this.logLeave('PopOnRoot');
    this.lastPushedPage = null;
    this.currentPageType = null;
    this.navOptions.direction = 'back';
    this.navOptions.animation = 'md-transition';
    const navCtrl = this.app.getRootNavs()[0];
    const transitionPromise = navCtrl.pop(this.navOptions);
    this.setTransition(transitionPromise);
    this.transitionPromise.then(() => {
      const next = this.app.getRootNavs()[0];
      if (next.length() > 0)
        this.logEnter({ page: this.extractPageName(next._views[next.length() - 1]) }, null, 'PopOnRoot', log);
    });
    return this.transitionPromise;
  }


  modalDismiss(viewCtrl, navParams) {
    const modalDismiss = navParams.get('modalDismiss');
    if (modalDismiss)
      modalDismiss();
    else
      viewCtrl.dismiss();
  }

}





// WEBPACK FOOTER //
// ./src/providers/route/route.ts